import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { X, Trash2, Globe, Plus, Send } from "lucide-react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { nok } from "@/pages/admin/budsjett/budsjettUtils";
import { PlanEditor } from "@/pages/admin/budsjett/PlanEditor";
import { SPONSOR_STAGES, sponsorStageFarge, TIERS, fmtSiden, datoIdag } from "./eventosUtils";
import { PakkeVelger } from "./PakkeVelger";
import { ReadinessBadge, ReadinessListe } from "./Readiness";
import ProposalSection from "./ProposalSection";

const Seksjon = ({ label, children }) => (
  <div className="mt-6">
    <p className="text-[10px] font-semibold uppercase tracking-[0.16em] text-neutral-400 mb-2">{label}</p>
    {children}
  </div>
);

const Felt = ({ label, verdi, onLagre, testid, textarea, type = "text" }) => {
  const [v, setV] = useState(verdi ?? "");
  useEffect(() => { setV(verdi ?? ""); }, [verdi]);
  const Tag = textarea ? "textarea" : "input";
  return (
    <label className="adm-label">
      {label}
      <Tag
        value={v}
        type={textarea ? undefined : type}
        rows={textarea ? 3 : undefined}
        onChange={(e) => setV(e.target.value)}
        onBlur={() => String(v) !== String(verdi ?? "") && onLagre(type === "number" ? Number(v) || 0 : v)}
        className="adm-input"
        data-testid={testid}
      />
    </label>
  );
};

export default function PartnerDrawer({ partner, members, aktor, onPatch, onDelete, onLukk }) {
  const [saker, setSaker] = useState(null);
  const [nySak, setNySak] = useState("");
  const [nyLev, setNyLev] = useState("");
  const nav = useNavigate();

  useEffect(() => {
    const esc = (e) => { if (e.key === "Escape") onLukk(); };
    window.addEventListener("keydown", esc);
    return () => window.removeEventListener("keydown", esc);
  }, [onLukk]);

  useEffect(() => {
    let aktiv = true;
    api.get("/admin/tasks").then(({ data }) => {
      if (!aktiv) return;
      setSaker((data.tasks || []).filter((t) => (t.linkedEntities || []).some((le) => le.type === "partner" && le.id === partner.id)));
    }).catch(() => setSaker([]));
    return () => { aktiv = false; };
  }, [partner.id]);

  const company = partner.company || {};
  const patchCompany = (felt) => (v) => onPatch({ company: { [felt]: v } });
  const deliverables = partner.deliverables || [];
  const quota = partner.ticketQuota || { total: 0, used: 0 };

  const lagNySak = async () => {
    const t = nySak.trim();
    if (!t) return;
    try {
      const { data } = await api.post("/admin/tasks", {
        title: t,
        actor: aktor,
        linkedEntities: [{ type: "partner", id: partner.id, label: company.name }],
      });
      setSaker((prev) => [data.task, ...(prev || [])]);
      setNySak("");
      toast.success("Sak opprettet og koblet");
    } catch (e) {
      toast.error("Kunne ikke opprette sak");
    }
  };

  const leggTilLeveranse = () => {
    const t = nyLev.trim();
    if (!t) return;
    onPatch({ deliverables: [...deliverables, { text: t, done: false }] });
    setNyLev("");
  };

  const aktivitet = [...(partner.activity || [])].reverse();

  return (
    <div className="fixed inset-0 z-50 flex justify-end" data-testid="partner-drawer">
      <div className="sak-backdrop absolute inset-0 bg-black/25" onClick={onLukk} />
      <aside className="sak-drawer relative w-full max-w-[600px] h-full bg-white shadow-2xl border-l border-[#E7E4DB] flex flex-col">
        <div className="px-6 pt-5 pb-4 border-b border-neutral-100 shrink-0">
          <div className="flex items-start justify-between gap-3">
            <div>
              <h2 className="text-[19px] font-semibold leading-tight" data-testid="partner-drawer-navn">{company.name}</h2>
              <p className="text-[12px] text-neutral-400 mt-0.5">
                {[partner.packageName, partner.value ? nok(partner.value) + " eks. mva" : null].filter(Boolean).join(" · ") || "—"}
              </p>
            </div>
            <div className="flex items-center gap-1 shrink-0">
              <button
                type="button"
                onClick={() => onPatch({ published: !partner.published })}
                className={`inline-flex items-center gap-1.5 text-[11.5px] font-medium rounded-lg px-2.5 py-1.5 border transition-colors ${
                  partner.published
                    ? "bg-neutral-900 text-white border-neutral-900"
                    : "text-neutral-500 border-[#DED9CD] hover:text-neutral-800"
                }`}
                data-testid="partner-publiser-btn"
                title="Styrer om logoen vises på nettsiden"
              >
                <Globe size={12} /> {partner.published ? "Publisert" : "Ikke publisert"}
              </button>
              <button type="button" onClick={onDelete} className="p-1.5 text-neutral-400 hover:text-red-600 hover:bg-red-50 rounded-md transition-colors" data-testid="partner-slett-btn">
                <Trash2 size={15} />
              </button>
              <button type="button" onClick={onLukk} className="p-1.5 text-neutral-400 hover:text-neutral-800 hover:bg-neutral-100 rounded-md transition-colors" data-testid="partner-drawer-lukk">
                <X size={17} />
              </button>
            </div>
          </div>
          <div className="flex items-center gap-3 mt-3">
            <select
              value={partner.stage}
              onChange={(e) => onPatch({ stage: e.target.value })}
              className="text-[12.5px] font-medium border rounded-lg px-2.5 py-1.5 bg-white focus:outline-none transition-colors"
              style={{ borderColor: sponsorStageFarge(partner.stage), color: sponsorStageFarge(partner.stage) }}
              data-testid="partner-stage-select"
            >
              {SPONSOR_STAGES.map((s) => (
                <option key={s.k} value={s.k}>{s.label}</option>
              ))}
            </select>
            <ReadinessBadge computed={partner.computed} size="md" />
          </div>
          <div className="flex items-center gap-1.5 mt-2.5 flex-wrap">
            <button
              type="button"
              onClick={() => onPatch({ stage: "verbal", signal: null, ball: null })}
              className="text-[11px] font-semibold rounded-full px-2.5 py-1 border transition-all"
              style={partner.stage === "verbal" || partner.stage === "signed"
                ? { background: "#10B981", borderColor: "#10B981", color: "#fff" }
                : { borderColor: "#E7E4DB", color: "#10B981" }}
              title="Sett Muntlig ja"
              data-testid="partner-signal-yes"
            >
              Ja
            </button>
            <button
              type="button"
              onClick={() => onPatch({ signal: partner.signal === "maybe" ? null : "maybe", ball: partner.signal === "maybe" ? null : "us" })}
              className="text-[11px] font-semibold rounded-full px-2.5 py-1 border transition-all"
              style={partner.signal === "maybe"
                ? { background: "#D97706", borderColor: "#D97706", color: "#fff" }
                : { borderColor: "#E7E4DB", color: "#D97706" }}
              title="Sett/fjern kanskje"
              data-testid="partner-signal-maybe"
            >
              Kanskje
            </button>
            <button
              type="button"
              onClick={() => onPatch(partner.stage === "declined"
                ? { stage: "dialogue", declineReason: "", signal: null }
                : { stage: "declined", signal: null, ball: null })}
              className="text-[11px] font-semibold rounded-full px-2.5 py-1 border transition-all"
              style={partner.stage === "declined"
                ? { background: "#DC2626", borderColor: "#DC2626", color: "#fff" }
                : { borderColor: "#E7E4DB", color: "#DC2626" }}
              title={partner.stage === "declined" ? "Gjenåpne — tilbake til Dialog" : "Sett Avslått (grunn settes i Outreach-listen)"}
              data-testid="partner-signal-no"
            >
              Nei
            </button>
            <button
              type="button"
              onClick={() => { onPatch({ lastContact: datoIdag(), ball: "them" }); toast.success("Kontakt logget"); }}
              className="inline-flex items-center gap-1.5 text-[11px] font-medium text-neutral-500 rounded-full px-2.5 py-1 border border-[#E7E4DB] hover:border-neutral-400 hover:text-neutral-800 transition-colors"
              data-testid="partner-logg-kontakt"
            >
              <Send size={10.5} /> Logg kontakt
            </button>
            <label className="inline-flex items-center gap-1.5 text-[11px] text-neutral-400" title="Oppfølgingsdato">
              Følg opp
              <input
                type="date"
                value={partner.followUp || ""}
                onChange={(e) => onPatch({ followUp: e.target.value || "" })}
                className="text-[11px] border border-[#E7E4DB] rounded-lg px-1.5 py-0.5 bg-white text-neutral-600 focus:outline-none focus:border-neutral-400 transition-colors"
                data-testid="partner-folgopp"
              />
            </label>
            {partner.lastContact && (
              <span className="bud-mono text-[10.5px] text-neutral-400 tabular-nums" data-testid="partner-sist-kontakt">
                sist {fmtSiden(partner.lastContact)}
              </span>
            )}
          </div>
        </div>

        <div className="flex-1 overflow-y-auto px-6 pb-6">
          <Seksjon label="Fulfillment">
            <ReadinessListe computed={partner.computed} onSett={(r) => onPatch({ readiness: r })} testidPrefix="partner" />
          </Seksjon>

          {partner.published && (
            <Seksjon label="Nettside (logostripen)">
              <div className="grid grid-cols-2 gap-3">
                <label className="adm-label">
                  Tier
                  <select
                    value={partner.publishedTier || "gold"}
                    onChange={(e) => onPatch({ publishedTier: e.target.value })}
                    className="adm-input"
                    data-testid="partner-tier-select"
                  >
                    {TIERS.map((t) => (
                      <option key={t.k} value={t.k}>{t.label}</option>
                    ))}
                  </select>
                </label>
                <label className="adm-label">
                  Visningsrekkefølge
                  <input
                    type="number"
                    defaultValue={partner.publishedOrder ?? 99}
                    onBlur={(e) => Number(e.target.value) !== partner.publishedOrder && onPatch({ publishedOrder: Number(e.target.value) })}
                    className="adm-input"
                    data-testid="partner-rekkefolge"
                  />
                </label>
              </div>
              <p className="text-[11px] text-neutral-400 mt-2">Logoen hentes fra selskapets logo-URL under.</p>
            </Seksjon>
          )}

          <Seksjon label="Avtale">
            <div className="grid grid-cols-2 gap-3">
              <PakkeVelger verdi={partner.packageName} onVelg={(v) => onPatch({ packageName: v })} testid="partner-pakke" />
              <Felt label="Verdi eks. mva (NOK)" verdi={partner.value} type="number" onLagre={(v) => onPatch({ value: v })} testid="partner-verdi" />
              <label className="adm-label">
                Sannsynlighet (overstyr %)
                <input
                  type="number"
                  min={0}
                  max={100}
                  defaultValue={partner.probabilityOverride ?? ""}
                  placeholder="Std fra stadium"
                  onBlur={(e) => {
                    const ny = e.target.value === "" ? null : Number(e.target.value);
                    if (ny !== (partner.probabilityOverride ?? null)) onPatch({ probabilityOverride: ny });
                  }}
                  className="adm-input"
                  data-testid="partner-prob"
                />
              </label>
              <label className="adm-label">
                Intern ansvarlig
                <select
                  value={partner.ownerId || ""}
                  onChange={(e) => onPatch({ ownerId: e.target.value || null })}
                  className="adm-input"
                  data-testid="partner-owner-select"
                >
                  <option value="">Ingen</option>
                  {(members || []).map((m) => (
                    <option key={m.id} value={m.id}>{m.name}</option>
                  ))}
                </select>
              </label>
            </div>
          </Seksjon>

          <ProposalSection companyId={company.id} aktor={aktor} />

          <Seksjon label="Selskap">
            <div className="grid grid-cols-2 gap-3">
              <Felt label="Selskapsnavn" verdi={company.name} onLagre={patchCompany("name")} testid="partner-firma-navn" />
              <Felt label="Nettside" verdi={company.website} onLagre={patchCompany("website")} testid="partner-firma-web" />
              <Felt label="Kontaktperson" verdi={company.contactName} onLagre={patchCompany("contactName")} testid="partner-kontakt-navn" />
              <Felt label="E-post" verdi={company.contactEmail} onLagre={patchCompany("contactEmail")} testid="partner-kontakt-epost" />
              <Felt label="Telefon" verdi={company.contactPhone} onLagre={patchCompany("contactPhone")} testid="partner-kontakt-telefon" />
              <Felt label="Logo-URL" verdi={company.logoUrl} onLagre={patchCompany("logoUrl")} testid="partner-logo-url" />
            </div>
          </Seksjon>

          <Seksjon label={`Leveranser (${deliverables.filter((d) => d.done).length}/${deliverables.length})`}>
            {deliverables.map((d) => (
              <div key={d.id} className="flex items-center gap-2.5 py-1 group" data-testid={`partner-leveranse-${d.id}`}>
                <button
                  type="button"
                  onClick={() => onPatch({ deliverables: deliverables.map((x) => (x.id === d.id ? { ...x, done: !x.done } : x)) })}
                  className={`w-[15px] h-[15px] rounded-full border flex items-center justify-center shrink-0 text-[9px] font-bold ${
                    d.done ? "bg-emerald-500 border-emerald-500 text-white" : "border-neutral-300 text-transparent"
                  }`}
                  data-testid={`partner-leveranse-toggle-${d.id}`}
                >
                  {d.done ? "✓" : ""}
                </button>
                <span className={`flex-1 text-[12.5px] ${d.done ? "text-neutral-400 line-through" : "text-neutral-800"}`}>{d.text}</span>
                <button
                  type="button"
                  onClick={() => onPatch({ deliverables: deliverables.filter((x) => x.id !== d.id) })}
                  className="opacity-0 group-hover:opacity-100 text-neutral-300 hover:text-red-500 transition-all"
                  data-testid={`partner-leveranse-slett-${d.id}`}
                >
                  <X size={12} />
                </button>
              </div>
            ))}
            <div className="flex items-center gap-2 mt-1.5">
              <Plus size={13} className="text-neutral-300 shrink-0" />
              <input
                value={nyLev}
                onChange={(e) => setNyLev(e.target.value)}
                onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); leggTilLeveranse(); } }}
                placeholder="Ny leveranse — Enter"
                className="flex-1 text-[12.5px] py-1 bg-transparent text-neutral-900 focus:outline-none placeholder:text-neutral-300"
                data-testid="partner-ny-leveranse-input"
              />
            </div>
          </Seksjon>

          <Seksjon label="Billetter & hospitality">
            <div className="grid grid-cols-3 gap-3">
              <Felt label="Billettkvote" verdi={quota.total} type="number" onLagre={(v) => onPatch({ ticketQuota: { total: v } })} testid="partner-kvote-total" />
              <Felt label="Brukt" verdi={quota.used} type="number" onLagre={(v) => onPatch({ ticketQuota: { used: v } })} testid="partner-kvote-brukt" />
              <Felt label="Middagsplasser" verdi={partner.dinnerSeats} type="number" onLagre={(v) => onPatch({ dinnerSeats: v })} testid="partner-middag" />
            </div>
          </Seksjon>

          <Seksjon label="Betalingsplan (synkes med budsjettets cash flow)">
            <PlanEditor plan={partner.paymentPlan || []} onChange={(p) => onPatch({ paymentPlan: p })} testid="partner-plan-editor" />
          </Seksjon>

          <Seksjon label="Interne notater">
            <Felt label="" verdi={partner.notes} onLagre={(v) => onPatch({ notes: v })} testid="partner-notater" textarea />
          </Seksjon>

          <Seksjon label={`Saker (${saker == null ? "…" : saker.length})`}>
            {(saker || []).map((t) => (
              <p key={t.id} className="flex items-center gap-2 text-[12.5px] py-1" data-testid={`partner-sak-${t.id}`}>
                <span className={`w-1.5 h-1.5 rounded-full ${t.status === "done" ? "bg-emerald-500" : "bg-amber-500"}`} />
                <span className={t.status === "done" ? "text-neutral-400 line-through" : "text-neutral-700"}>{t.title}</span>
                {t.dueDate && <span className="text-[10.5px] text-neutral-400 ml-auto">{t.dueDate}</span>}
              </p>
            ))}
            <div className="flex items-center gap-2 mt-1.5">
              <Plus size={13} className="text-neutral-300 shrink-0" />
              <input
                value={nySak}
                onChange={(e) => setNySak(e.target.value)}
                onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); lagNySak(); } }}
                placeholder="Ny sak koblet til denne partneren — Enter"
                className="flex-1 text-[12.5px] py-1 bg-transparent text-neutral-900 focus:outline-none placeholder:text-neutral-300"
                data-testid="partner-ny-sak-input"
              />
            </div>
          </Seksjon>

          <Seksjon label="Budsjett">
            <button
              type="button"
              onClick={() => nav("/admin/budsjett")}
              className="text-[12.5px] text-neutral-600 underline underline-offset-2 hover:text-neutral-900 transition-colors"
              data-testid="partner-til-budsjett"
            >
              Avtalen inngår i sponsor-pipelinen i budsjettet — åpne budsjett →
            </button>
          </Seksjon>

          {aktivitet.length > 0 && (
            <Seksjon label="Aktivitet">
              <div className="space-y-1">
                {aktivitet.slice(0, 8).map((a, i) => (
                  <p key={i} className="text-[11px] text-neutral-400 leading-relaxed">
                    <span className="text-neutral-600 font-medium">{a.actor}</span> · {a.text}
                    <span className="ml-1.5 text-neutral-300">{new Date(a.at).toLocaleDateString("nb-NO", { day: "numeric", month: "short" })}</span>
                  </p>
                ))}
              </div>
            </Seksjon>
          )}
        </div>
      </aside>
    </div>
  );
}
